import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { populateServices } from 'src/services';
import { LoggerServiceFactory } from 'src/services/LoggerService';
import { getClient } from './apolloClient';
import Idp from './Idp';
import './polyfills';

// Create browser history to use in the Redux store
const baseUrl = document
  .getElementsByTagName('base')[0]
  .getAttribute('href') as string;

// Declare the container element
const container = document.getElementById('root');

const state = window.__INITIAL_SERVER_STATE__;

// This hurts my eyes so much but for now we need to inject the mocks
// component into the Apps main tree in order to be able to use the same context.
let optionalChildren: JSX.Element | null = null;
if (import.meta.env.MODE === 'development') {
  const { Mocks } = await import('../mocks/components/Mocks');
  optionalChildren = (
    <React.Suspense fallback={null}>
      <Mocks />
    </React.Suspense>
  );
}

if (container) {
  const root = createRoot(container);
  // Mount to root element
  root.render(
    <React.StrictMode>
      <BrowserRouter basename={baseUrl}>
        <ApolloProvider
          client={getClient(state?.graphQlBaseUrl, state?.clientName?.iv)}
        >
          <Idp children={optionalChildren} />
        </ApolloProvider>
      </BrowserRouter>
    </React.StrictMode>,
  );
  setTimeout(() => {
    document.getElementById('preloaded-container')?.remove();
  }, 0);
}

// Set up production services
populateServices();

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  normalizeDepth: 8, // Adjust [object] depth as required here, our state is quite deep so accommodating that here
  integrations: [
    Sentry.breadcrumbsIntegration({
      // No need to log console entries in Sentry
      console: false,
      dom: true,
      fetch: true,
      history: true,
      xhr: true,
    }),
  ],
});

// Logging this here in the index file to capture initial page load
// very useful for Sentry logging to be able to correlate logs with
// the IDP server logs. So all /ui/login requests get a "Login" message
// in Sentry, every /ui/selectaccount requests get a "SelectAccount" message, etc.
const logger = LoggerServiceFactory.create('Application');

if (location.pathname.includes('/ui/login')) {
  logger.message('Login');
} else if (location.pathname.includes('/ui/selectaccount')) {
  logger.message('SelectAccount');
} else if (location.pathname.includes('/ui/consent')) {
  logger.message('Consent');
} else if (location.pathname.includes('/ui/error')) {
  logger.message('Error');
} else {
  logger.warn(`Unsupported path ${location.pathname}`);
}
